/**
 * Cores e rótulos das roles (acessos) exibidas como po-tag.
 * Fonte única para o perfil do usuário (menu) e a Gestão de usuários, para as duas telas mostrarem a mesma cor.
 */
export interface RoleTag {
  label: string;
  color: string;
  /** Texto branco nas cores escuras; ausente = texto padrão do po-tag (escuro). */
  textColor?: string;
}

/**
 * Cores do tema PO-UI escuras demais para o texto padrão (escuro) do po-tag: contraste < 4.5:1.
 * Com texto branco ficam entre 4.8:1 e 7.7:1. As demais (01, 02, 08 a 12) já são legíveis com o texto padrão.
 */
const DARK_THEME_COLORS: ReadonlySet<string> = new Set(["color-03", "color-04", "color-05", "color-06", "color-07"]);
const WHITE = "#ffffff";

const FALLBACK_COLORS: ReadonlyArray<string> = [
  "color-01",
  "color-02",
  "color-03",
  "color-04",
  "color-05",
  "color-06",
  "color-07",
  "color-08",
  "color-09",
  "color-10",
  "color-11",
  "color-12",
];

export const KNOWN_ROLE_TAGS: Readonly<Record<string, RoleTag>> = {
  superuser: { label: "Superuser", color: "color-08" },
  user: { label: "User", color: "color-08" },
  guest: { label: "Guest", color: "color-06" },
  backoffice: { label: "Backoffice", color: "color-02" },
  tfs: { label: "TFS", color: "color-09" },
  apikeys: { label: "API Keys", color: "color-06" },
  team_account: { label: "Team Account", color: "color-03" },
  mcp: { label: "MCP", color: "color-01" },
  chat: { label: "Chat", color: "color-11" },
  "mcp:reports": { label: "Reports", color: "color-04" },
  "mcp:reports:documenta-ai": { label: "Reports · Documenta AI", color: "color-04" },
  "smartreview.member": { label: "Smart Review · Member", color: "color-05" },
  "smartreview.run": { label: "Smart Review · Run", color: "color-05" },
  "smartreview.audit": { label: "Smart Review · Audit", color: "color-05" },
  "smartreview.admin": { label: "Smart Review · Admin", color: "color-05" },
  sonaradmin: { label: "SonarQube · Admin", color: "#290042" },
};

/** Role desconhecida: cor estável derivada do nome (a mesma role sempre na mesma cor). */
function fallbackColor(role: string): string {
  let hash = 0;
  for (let i = 0; i < role.length; i++) {
    hash = (hash * 31 + role.charCodeAt(i)) >>> 0;
  }
  return FALLBACK_COLORS[hash % FALLBACK_COLORS.length];
}

/** Cor customizada (#rrggbb): texto branco se o fundo for escuro (luminância relativa < 0.18). */
function isDarkHex(color: string): boolean {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(color);
  if (!m) return false;
  const [r, g, b] = m.slice(1).map((h) => {
    const c = parseInt(h, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 0.18;
}

export function roleTagTextColor(color: string): string | undefined {
  return DARK_THEME_COLORS.has(color) || isDarkHex(color) ? WHITE : undefined;
}

export function resolveRoleTag(role: string): RoleTag {
  const tag = KNOWN_ROLE_TAGS[role.toLowerCase()] ?? { label: role, color: fallbackColor(role) };
  const textColor = roleTagTextColor(tag.color);
  return textColor ? { ...tag, textColor } : tag;
}

export function roleTagColor(role: string): string {
  return resolveRoleTag(role).color;
}
